import { useEffect, useMemo, useState } from 'react'
import Table from '../../common/Table'
import ViewLegislationModal from '../modals/ViewLegislationModal'
import ArchiveLegislationModal from '../modals/ArchiveLegislationModal'
import LegislationModal from '../modals/LegislationModal'
import LegislationFileViewer from '../modals/LegislationFileViewer'
import DateTimeCell from '../../common/DateTimeCell'
import { ArchiveIcon, EyeIcon, PencilIcon, PrinterIcon } from 'lucide-react'
import { useFetch, useSubmission } from '../../../hooks/useCallApi'
import { StatusBadge } from '../../common/status'
import toast from 'react-hot-toast'

function ResolutionOrdinanceTable({
  search,
  typeFilter,
  committeeFilter,
  statusFilter,
  startDate,
  endDate,
  header,
  activeTab,
}) {
  const [selectedLegislation, setSelectedLegislation] = useState(null)
  const [modals, setModals] = useState({
    view: false,
    edit: false,
    archive: false,
    print: false,
  })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, currentPage: 1 }))
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  const params = useMemo(
    () => ({
      per_page: pagination.perPage,
      page: pagination.currentPage,
      section: 'resolution_ordinance',
      search: search || undefined,
      doc_type: typeFilter || undefined,
      committee_id: committeeFilter || undefined,
      status: statusFilter || undefined,
      filed_from: startDate || undefined,
      filed_to: endDate || undefined,
    }),
    [
      pagination,
      search,
      typeFilter,
      committeeFilter,
      statusFilter,
      startDate,
      endDate,
    ]
  )

  const {
    data: legislativeData,
    isLoading,
    refetch,
  } = useFetch(['resolution-ordinance', params], (api) =>
    api.admin.legislation.getTabView(params)
  )
  const { data: legislationDetails, refetch: refetchLegislation } = useFetch(
    ['legislation-details', selectedLegislation?.legislative_document_id],
    (api) =>
      api.admin.legislation.getLegislation(
        selectedLegislation?.legislative_document_id
      ),
    {
      enabled: !!selectedLegislation?.legislative_document_id && modals.print,
    }
  )

  useEffect(() => {
    if (activeTab === 'Resolution & Ordinance') refetch()
  }, [activeTab, refetch])

  const { mutateAsync: archiveLegislation } = useSubmission(
    ['archive-legislation'],
    (api, args) => api.admin.legislation.archive(args)
  )

  const tableRows = Array.isArray(legislativeData?.data?.data)
    ? legislativeData?.data?.data
    : []

  const openModal = async (type, item) => {
    setSelectedLegislation(item)

    if (type === 'print') {
      try {
        setModals((prev) => ({ ...prev, [type]: true }))
        return
      } catch (err) {
        console.error('Failed to fetch legislation details', err)
        return
      }
    }

    setModals((prev) => ({ ...prev, [type]: true }))
  }

  const closeModal = (type) => setModals((prev) => ({ ...prev, [type]: false }))

  const handleArchiveConfirm = async () => {
    if (!selectedLegislation) return

    try {
      toast.loading('Archiving legislation...', { id: 'archive' })

      await archiveLegislation({
        id: selectedLegislation.legislative_document_id,
      })

      toast.success('Legislation archived successfully!', { id: 'archive' })

      closeModal('archive')
      refetch()
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to archive legislation',
        { id: 'archive' }
      )
    }
  }

  const handlePageChange = (page, perPage) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      perPage: perPage || prev.perPage,
    }))
  }

  const formatType = (type) => {
    if (!type) return '—'
    const str = type.toString().toLowerCase()
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const columns = [
    {
      key: 'legislation_type',
      label: 'Legislation Type',
      render: (_, item) => (
        <span className='text-sm font-medium text-gray-900'>
          {formatType(item?.legislation_type)}
        </span>
      ),
    },
    {
      key: 'legislation_info',
      label: 'Legislation',
      render: (_, item) => (
        <div className='space-y-1 '>
          <div
            className='text-sm text-gray-700 font-medium line-clamp-2 whitespace-normal'
            title={item?.title}
          >
            {item?.title || 'Untitled'}
          </div>
          <div className='text-xs text-gray-500 truncate'>
            {item?.committee?.committee_name ||
              item?.committee?.name ||
              item?.committee_name ||
              item?.committee ||
              '—'}
          </div>
          <div className='text-xs text-gray-500'>
            {item?.doc_number || item?.legislation_number || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, item) => <StatusBadge status={item?.status} />,
    },
    {
      key: 'filing_date',
      label: 'Filing Date',
      render: (_, item) => (
        <span className='text-sm text-gray-500'>
          <DateTimeCell value={item?.filing_date} showTime={false} />
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, item) => (
        <div className='flex items-center space-x-1'>
          <button
            onClick={() => openModal('view', item)}
            className='text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110'
            title='View Details'
          >
            <EyeIcon className='h-4 w-4' />
          </button>
          <button
            onClick={() => openModal('edit', item)}
            className='text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110'
            title='Edit'
          >
            <PencilIcon className='h-4 w-4' />
          </button>
          <button
            onClick={() => openModal('print', item)}
            className='text-green-600 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50 transition-all duration-200 hover:scale-110'
            title='Print'
          >
            <PrinterIcon className='h-4 w-4' />
          </button>
          <button
            onClick={() => openModal('archive', item)}
            className='text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-100 transition-all duration-200 hover:scale-110'
            title='Archive'
          >
            <ArchiveIcon className='h-4 w-4' />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div>
      {modals.print && (
        <LegislationFileViewer
          selectedLegislation={legislationDetails?.data}
          onClose={() => closeModal('print')}
          isLoading={!legislationDetails?.data}
        />
      )}
      <Table
        header={header}
        columns={columns}
        data={tableRows}
        getRowKey={(row) => row.id}
        pagination={{
          currentPage: pagination.currentPage,
          perPage: pagination.perPage,
          lastPage: legislativeData?.data?.meta?.last_page || 1,
          total: legislativeData?.data?.meta?.total || 0,
        }}
        onPageChange={handlePageChange}
        onPerPageChange={(value) =>
          setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }))
        }
        isLoading={isLoading}
      />

      <ViewLegislationModal
        isOpen={modals.view}
        onClose={() => closeModal('view')}
        legislation={selectedLegislation}
      />

      <LegislationModal
        isOpen={modals.edit}
        onClose={() => closeModal('edit')}
        legislation={selectedLegislation}
        mode='edit'
        onSuccess={refetch}
        refetch={refetch}
      />

      <ArchiveLegislationModal
        isOpen={modals.archive}
        onClose={() => closeModal('archive')}
        legislation={selectedLegislation}
        onArchive={handleArchiveConfirm}
      />
    </div>
  )
}

export default ResolutionOrdinanceTable
